import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { ApiClient } from '../utils/apiClient';

/**
 * URLValidationInput Component
 * 
 * Provides URL validation with backend processing and UI feedback.
 * Shows loading states, error handling, and verified URL block display.
 * 
 * Requirements: 13.1, 13.2, 13.3, 13.4, 13.5, 13.6, 13.7
 */
const URLValidationInput = ({
  value = '',
  onValidated,
  onRemove,
  disabled = false,
  placeholder = 'Enter URL (e.g., https://example.com/docs)',
  className = ''
}) => {
  const { getToken } = useAuth();
  const { showError } = useToast();
  const { theme } = useTheme();
  const apiClient = useRef(new ApiClient(getToken)).current;

  // Component state
  const [inputValue, setInputValue] = useState(value || '');
  const [validatedUrl, setValidatedUrl] = useState(null);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);

  // Validation timeout ref for debouncing
  const validationTimeoutRef = useRef(null);

  const isDark = theme === 'dark';

  // Initialize with existing validated URL if provided
  useEffect(() => {
    if (value && value.trim()) {
      // If we have a value prop and no validated URL yet, set it as validated
      if (!validatedUrl) {
        setValidatedUrl({
          url: value.trim(),
          title: value.trim(),
          validated_at: new Date().toISOString(),
          fromExisting: true
        });
        setInputValue(''); // Clear input when showing validated URL
      }
    } else if (!value && validatedUrl && validatedUrl.fromExisting) {
      // If value prop is cleared and we have an existing validated URL, clear it
      setValidatedUrl(null);
    }
  }, [value]);

  /**
   * Validate URL using backend endpoint
   * Requirement: 13.1, 13.2 - Backend validation with accessibility checks
   */
  const validateUrl = async (url) => {
    if (!url || !url.trim()) {
      setError(null);
      return;
    }

    const trimmedUrl = url.trim();
    
    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    setValidating(true);
    setError(null);

    try {
      const response = await apiClient.post('/api/knowledge-base/validate-url', {
        url: trimmedUrl
      });

      if (response && response.success && response.accessible) {
        // URL is valid and accessible
        const urlData = {
          url: trimmedUrl,
          title: response.preview?.title || trimmedUrl,
          description: response.preview?.description || null,
          contentType: response.preview?.contentType || null,
          validated_at: new Date().toISOString(),
          details: response.details || {}
        };

        setValidatedUrl(urlData);
        setPreview(response.preview || null);
        setInputValue(''); // Clear input field
        setError(null);

        // Notify parent component
        if (onValidated) {
          onValidated(urlData);
        }
      } else {
        // URL validation failed
        const errorMessage = response?.error || 'URL validation failed';
        setError(errorMessage);
        setValidatedUrl(null);
        setPreview(null);
      }
    } catch (err) {
      console.error('URL validation error:', err);
      const errorMessage = err.message || 'Failed to validate URL. Please check the URL and try again.';
      setError(errorMessage);
      setValidatedUrl(null);
      setPreview(null);
      showError(errorMessage);
    } finally {
      setValidating(false);
    }
  };

  /**
   * Handle input change with debounced validation
   * Requirement: 13.1 - Real-time validation feedback
   */
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setError(null);

    // Clear previous timeout
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }

    // Don't validate empty input
    if (!newValue.trim()) {
      setValidating(false);
      return;
    }

    // Debounce validation
    validationTimeoutRef.current = setTimeout(() => {
      validateUrl(newValue);
    }, 1000);
  };

  /**
   * Handle manual validation trigger
   * Requirement: 13.4 - Manual validation trigger
   */
  const handleValidateClick = () => {
    if (inputValue.trim()) {
      validateUrl(inputValue);
    }
  };

  /**
   * Handle Enter key press
   * Requirement: 13.4 - Keyboard interaction
   */
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleValidateClick();
    }
  };

  /**
   * Handle URL removal
   * Requirement: 13.6, 13.7 - URL removal functionality
   */
  const handleRemoveValidated = () => {
    setValidatedUrl(null);
    setPreview(null);
    setError(null);
    setInputValue('');
    
    if (onRemove) {
      onRemove();
    }
  };

  /**
   * Get validation status icon
   */
  const getValidationIcon = () => {
    if (validating) {
      return (
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
      );
    }

    if (error) {
      return (
        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      );
    }

    if (validatedUrl) {
      return (
        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      );
    }

    return null;
  };

  return (
    <div className={`url-validation-input ${className}`}>
      {!validatedUrl ? (
        <div className="space-y-2">
          {/* URL Input Field */}
          <div className="relative">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input
                  type="url"
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={placeholder}
                  disabled={disabled || validating}
                  className={`
                    w-full px-3 py-2 pr-10 border rounded-md transition-colors
                    ${isDark 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:border-teal-500 focus:ring-teal-500' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:border-teal-500 focus:ring-teal-500'
                    }
                    ${error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}
                    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                />
                
                {/* Validation Status Icon */}
                {(validating || error || validatedUrl) && (
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    {getValidationIcon()}
                  </div>
                )}
              </div>

              {/* Validate Button */}
              <button
                type="button"
                onClick={handleValidateClick}
                disabled={!inputValue.trim() || validating || disabled}
                className={`
                  px-4 py-2 rounded-md font-medium transition-colors
                  ${validating || !inputValue.trim() || disabled
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed dark:bg-gray-600 dark:text-gray-400'
                    : 'bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2'
                  }
                `}
              >
                {validating ? 'Validating...' : 'Add URL'}
              </button>
            </div>

            {/* Validation Status Message */}
            {validating && (
              <div className="text-sm text-blue-600 mt-1 flex items-center gap-1">
                <div className="animate-spin rounded-full h-3 w-3 border-b border-blue-600" />
                <span>Validating URL accessibility...</span>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className={`
              p-3 rounded-md border-l-4 border-red-500
              ${isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'}
            `}>
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-medium">Validation Error</span>
              </div>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}
        </div>
      ) : (
        /* Verified URL Block */
        <div className={`
          verified-url-block rounded-md border-l-4 border-green-500 p-4
          ${isDark ? 'bg-green-900/20' : 'bg-green-50'}
        `}>
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              {/* Success Header */}
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-green-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className={`text-sm font-medium ${isDark ? 'text-green-300' : 'text-green-800'}`}>
                  URL Verified
                </span>
                {validatedUrl.fromExisting && (
                  <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                    Previously saved
                  </span>
                )}
              </div>

              {/* URL Display */}
              <div className="space-y-1">
                <p className={`text-sm break-all ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {validatedUrl.url}
                </p>
                
                {/* Title and Description */}
                {validatedUrl.title && validatedUrl.title !== validatedUrl.url && (
                  <p className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Title: {validatedUrl.title}
                  </p>
                )}
                
                {validatedUrl.description && (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {validatedUrl.description}
                  </p>
                )}

                {/* Content Type */}
                {validatedUrl.contentType && (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    Content Type: {validatedUrl.contentType}
                  </p>
                )}

                {/* Validation Timestamp */}
                <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Validated: {new Date(validatedUrl.validated_at).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Remove Button */}
            <button
              type="button"
              onClick={handleRemoveValidated}
              disabled={disabled}
              className={`
                ml-3 p-1 rounded-full transition-colors flex-shrink-0
                ${disabled 
                  ? 'text-gray-400 cursor-not-allowed' 
                  : 'text-gray-400 hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                }
              `}
              title="Remove URL"
              aria-label="Remove validated URL"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default URLValidationInput;